<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <b-button id="view-button" class="custom-btn-secondary mr-1" @click="toggleView">
          <font-awesome-icon :icon="viewMode === 'table' ? 'th-large' : 'list'"/>
        </b-button>
        <b-tooltip target="view-button" triggers="hover" custom-class="custom-tooltip" noninteractive>
          {{ viewMode === 'table' ? 'Канбан' : 'Таблица' }}
        </b-tooltip>
        <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle="viewMode === 'table' ? 'sidebar-fields' : 'sidebar-kanban'" @click="onPopoverClose">
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
        <b-btn v-if="canEditRecruitTasks()" class="custom-btn-primary" :disabled="isBusy" @click="showModal(null, false)">
          <b-spinner v-if="isBusy" small/>
          <v-icon name="buttons/plus" :size="12"/>
          Новая заявка
        </b-btn>
      </div>
    </header>

    <div class="page-body">
      <div v-if="!isIntegrated" class="page-filters">
        <filters-drawer
          :hide-search="true"
          :is-busy="isBusy"
          :filter-tags="activeFilterTags"
          @remove-tag="removeFilterTag"
          @submit="onSearch"
          @clear="onClearFilters"
        >
          <b-col cols="12">
            <label class="small text-muted">Вакансии</label>
            <vacancy-multi-select
              id="vacancy-multi-select"
              ref="vacancy_multi_select"
              @onSelect="onSelectVacancy"
            />
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Дата открытия</label>
            <b-row class="align-items-center">
              <b-col>
                <b-form-input
                  id="input-start-opened-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.start_opened_date"
                />
              </b-col>
              <b-col cols="auto" class="px-2">
                <span>&mdash;</span>
              </b-col>
              <b-col>
                <b-form-input
                  id="input-end-opened-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.end_opened_date"
                />
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Дата закрытия</label>
            <b-row class="align-items-center">
              <b-col>
                <b-form-input
                  id="input-start-closed-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.start_closed_date"
                />
              </b-col>
              <b-col cols="auto" class="px-2">
                <span>&mdash;</span>
              </b-col>
              <b-col>
                <b-form-input
                  id="input-end-closed-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.end_closed_date"
                />
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Объекты</label>
            <location-multi-select
              id="location-multi-select"
              ref="location_multi_select"
              @onSelect="onSelectLocation"
            />
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Участники</label>
            <user-multi-select
              id="user-multi-select"
              ref="user_multi_select"
              title="Участники"
              @onSelect="onSelectUser"
            />
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Ответственные</label>
            <user-multi-select
              id="user-multi-select-assigned"
              ref="user_multi_select_assigned"
              title="Ответственные"
              @onSelect="onSelectUserAssigned"
            />
          </b-col>
          <b-col cols="12" class="mt-1">
            <b-row class="align-items-center">
              <b-col cols="6">
                <label class="small text-muted">Статус</label>
                <b-form-select
                  id="input-statuses"
                  size="sm"
                  v-model="filters.status_id"
                  :options="statusOptions"
                />
              </b-col>
              <b-col cols="6">
                <label class="small text-muted">Срочно</label>
                <b-form-select
                  id="input-urgency"
                  size="sm"
                  v-model="filters.urgency"
                >
                  <option :value="null">Все заявки</option>
                  <option :value="true">Да</option>
                  <option :value="false">Нет</option>
                </b-form-select>
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Теги</label>
            <recruit-tag-multi-select
              id="tag-multi-select"
              ref="tag_multi_select"
              @onSelect="onSelectTag"
            />
          </b-col>
        </filters-drawer>
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container" :class="{ 'border-0': viewMode === 'kanban' }">
          <b-table
            v-if="viewMode === 'table'"
            id="recruit-tasks"
            ref="recruit_tasks"
            class="custom-table mb-0 text-small"
            select-mode="multi"
            :tbody-tr-class="tbodyRowClass"
            sticky-header
            responsive
            :busy.sync="isBusy"
            :items="items"
            :fields="renderedFields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-clicked="rowClicked"
            @row-dblclicked="onRowDblClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="renderedFields" :rows="5"/>
            </template>

            <template #head(selected)="row">
              <b-form-checkbox
                class="mx-1"
                v-model="allSelected"
                :indeterminate="indeterminate"
                :disabled = "allSelectDisabled"
                size="md"
                @change="selectAllRows"
              />
            </template>

            <template #cell(selected)="row">
              <b-form-checkbox
                class="mx-1"
                size="md"
                :checked="row.item.selected"
                @click.native.stop
                @change="onRowDblClicked(row.item)"
              />
            </template>

            <template #cell(assigned)="row">
              {{ getAssignedName(row.item.assigned) }}
            </template>

            <template #cell(status_id)="row">
              {{ row.item.status_name || getStatusText(row.item.status_id) }}
            </template>

            <template #cell(opened_date)="row">
              {{formatTableDate(row.item.opened_date)}}
            </template>

            <template #cell(closed_date)="row">
              {{formatTableDate(row.item.closed_date)}}
            </template>

            <template #cell(urgency)="row">
              <span v-if="row.item.urgency" class="custom-tag tag-danger">Да</span>
              <span v-else>Нет</span>
            </template>

            <template #cell(tags)="row">
              <div v-if="row.value">
                <span v-for="tag in row.value" :key="tag.id" class="custom-tag mr-1 mb-1" :style="getBadgeStyle(tag.color)">
                  {{ tag.name }}
                </span>
              </div>
            </template>

            <template #cell(action)="row">
              <b-dropdown variant="link" toggle-class="text-decoration-none box-shadow-none" no-caret class="custom-dropdown">
                <template #button-content>
                  <v-icon name="buttons/ellipsis_vertical" :size="15"/>
                </template>
                <b-dropdown-item variant="success" @click.stop="showModal(row.item.id, true)">
                  <v-icon name="buttons/eye" :size="14" class="mr-2"/>
                  Смотреть
                </b-dropdown-item>
                <b-dropdown-item v-if="canEditRecruitTasks()" variant="primary" @click.stop="showModal(row.item.id, false)">
                  <v-icon name="buttons/edit" :size="14" class="mr-2"/>
                  Редактировать
                </b-dropdown-item>
                <b-dropdown-item v-if="canEditRecruitJobPostings()" variant="info" @click.stop="handleJobPosting(row.item.id)">
                  <v-icon name="navbar/job_postings" :size="14" class="mr-2"/>
                  Создать объявление
                </b-dropdown-item>
                <b-dropdown-item v-if="canEditRecruitTasks()" variant="danger" @click.stop="onRemove(row)">
                  <v-icon name="buttons/trash" :size="14" class="mr-2"/>
                  Удалить
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>

          <kanban
            v-if="viewMode === 'kanban'"
            :items="items"
            :columns="activeKanbanColumns"
            column-value="status_id"
            group-name="recruit-tasks"
            @status-updated="kanbanUpdateStatus"
          >
            <template #card="{ item }">
              <b-card
                no-body
                class="kb-card shadow-sm"
                :class="item.urgency ? 'border-danger' : 'border-default'"
                @dblclick="onRowDblClicked(item)"
              >
                <div class="d-flex justify-content-between align-items-center w-100">
                  <div class="font-weight-bold text-truncate mr-2">
                    {{ item.location_vacancy_name }}
                  </div>
                  <b-dropdown variant="link" toggle-class="text-decoration-none box-shadow-none flex-shrink-0" no-caret class="custom-dropdown p-0 m-0 custom-btn-secondary" style="width: 16px !important;">
                    <template #button-content>
                      <v-icon name="buttons/ellipsis_vertical" :size="13"/>
                    </template>
                    <b-dropdown-item variant="success" @click.stop="showModal(item.id, true)">
                      <v-icon name="buttons/eye" :size="14" class="mr-2"/>
                      Смотреть
                    </b-dropdown-item>
                    <b-dropdown-item v-if="canEditRecruitTasks()" variant="primary" @click.stop="showModal(item.id, false)">
                      <v-icon name="buttons/edit" :size="14" class="mr-2"/>
                      Редактировать
                    </b-dropdown-item>
                    <b-dropdown-item v-if="canEditRecruitJobPostings()" variant="info" @click.stop="handleJobPosting(item.id)">
                      <v-icon name="navbar/job_postings" :size="14" class="mr-2"/>
                      Создать объявление
                    </b-dropdown-item>
                    <b-dropdown-item v-if="canEditRecruitTasks()" variant="danger" @click="onRemove({ item })">
                      <v-icon name="buttons/trash" :size="14" class="mr-2"/>
                      Удалить
                    </b-dropdown-item>
                  </b-dropdown>
                </div>

                <div v-if="item.location_name" class="d-flex align-items-center w-100 small text-truncate">
                  <v-icon name="navbar/locations" :size="13" class="mr-2 flex-shrink-0"/>
                  <span class="text-truncate">{{ item.location_name }}</span>
                </div>

                <div class="d-flex align-items-center w-100 small">
                  <v-icon name="buttons/calendar" :size="13" class="mr-2 flex-shrink-0"/>
                  <span>{{ formatTableDate(item.opened_date) }}</span>
                  <span class="mx-1">–</span>
                  <span>{{ formatTableDate(item.closed_date) }}</span>
                </div>

                <div v-if="item.tags && item.tags.length" class="d-flex flex-wrap align-items-center w-100">
                  <span v-for="tag in item.tags" :key="tag.id" class="custom-tag mr-1 mb-1" :style="getBadgeStyle(tag.color)">
                    {{ tag.name }}
                  </span>
                </div>

                <hr class="w-100 m-0 border-light"/>

                <div class="d-flex justify-content-between align-items-center w-100 small text-muted">
                  <b-button variant="link" style="color: #1A9167 !important;" class="p-0 pl-1 mr-2 text-decoration-none" @click="onRowDblClicked(item)">
                    Открыть
                  </b-button>
                  <div class="text-truncate">
                    <font-awesome-icon icon="user" class="mr-1"/>
                    {{ getAssignedName(item.assigned) || 'Не назначен' }}
                  </div>
                </div>
              </b-card>
            </template>
          </kanban>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          >
            <template #footer-update>
              <span v-if="selected.length > 0" class="mr-2">
                Выбрано: <b>{{selected.length}}</b>
              </span>
              <span v-if="selected.length > 0" class="text-small vertical-divider"/>
              <span class="text-small text-muted mr-2">Записей: <b>{{ totalRows }}</b></span>
            </template>
            <template #footer-list>
              <span>
                <b-button v-if="selected.length > 0" id="update-status-button" ref="update_status_button" class="custom-btn-tag text-small" :style="{'--btn-color': '#85A5FF', '--btn-text': '#262626'}" @click="popoverShow = !popoverShow">
                  <v-icon name="buttons/swap" class="mr-1" :size="10"/>
                  Изменить статус
                </b-button>
              </span>
            </template>
          </pagination-footer>
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>Нет созданных заявок на подбор.</h5>
        <p>Создайте новую заявку прямо сейчас</p>
        <b-btn v-if="canEditRecruitTasks()" class="custom-btn-primary mt-1" :disabled="isBusy" @click="showModal(null, false)">
          <v-icon name="buttons/plus" :size="12" class="mr-1"/>
          Новая заявка
        </b-btn>
      </div>
    </div>

    <b-popover
      v-if="selected.length > 0"
      target="update-status-button"
      triggers="click"
      :show.sync="popoverShow"
      placement="auto"
      container="my-container"
      ref="popover_recruitTasks"
      @shown="onShown"
      @hidden="onHidden"
    >
      <template #title>
        <div class="d-flex justify-content-between align-items-center w-100">
          <small class="font-weight-bold text-muted">Назначить статус</small>
          <b-button @click="onPopoverClose" class="close pb-1" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </b-button>
        </div>
      </template>
      <b-form-select
        ref="allTasksStatusSelect"
        class="mb-2"
        size="sm"
        v-model="selectedNewStatus"
        :options="statusOptions"
      />
      <div class="d-flex justify-content-end">
        <b-button @click="onPopoverClose" size="sm" class="mr-1" variant="secondary">Закрыть</b-button>
        <b-button @click="onPopoverOk" size="sm" variant="success" :disabled="!selectedNewStatus || isBusy">Назначить</b-button>
      </div>
    </b-popover>

    <dynamic-tables
      storage-key="recruit_tasks_table_cols"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="id"
    />

    <dynamic-tables
      v-if="kanbanColumns.length || viewMode === 'kanban'"
      storage-key="recruit_tasks_kanban_cols"
      sidebar-fields="sidebar-kanban"
      :fields-model="kanbanColumns"
      field-key="key"
      allow-delete
      @update:fieldsModel="kanbanColumns = $event"
      @delete-col="handleDeleteKanbanColumn"
      @sidebar-closed="isAddingColumn = false"
    >
      <template #list-append>
        <div class="position-relative">
          <b-button
            v-if="!isAddingColumn"
            variant="link"
            class="text-decoration-none d-flex align-items-center justify-content-center w-100"
            style="color: #1A9167 !important;"
            @click="isAddingColumn = true"
          >
            <v-icon name="buttons/plus" :size="13" class="mr-2"/>
            <span class="text-small">Добавить новую колонку</span>
          </b-button>

          <div v-else>
            <recruit-task-status-select
              :exclude-ids="activeColumnIds"
              @onSelect="handleAddKanbanColumn"
            />
          </div>

          <div v-if="isAddingColumn" @click.stop="isAddingColumn = false"></div>
        </div>
      </template>
    </dynamic-tables>

    <recruit-task
      ref="recruit_task"
      :location-id="locationId"
      :location-name="locationName"
      :is-integrated="isIntegrated"
      @refresh="getItems"
      @modal-closed="onModalClosed"
    />

  </div>
</template>

<script>
import recruitTasks from "../../network/recruitTasks";
import catalogs from "../../network/catalogs";
import users from "../../network/users";
  import helpers from "../../utilites/helpers";
  import dateFormatter from "../../utilites/dateFormatter";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import Kanban from "../../utilites/commonUi/Kanban.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import draggable from 'vuedraggable';
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";
    import RecruitTask from "./RecruitTask"
    import LocationMultiSelect from "../Locations/LocationMultiSelect.vue";
    import UserMultiSelect from "../Users/UserMultiSelect.vue";
    import VacancyMultiSelect from "../Catalogs/Vacancies/VacancyMultiSelect.vue";
    import RecruitTagMultiSelect from "../Catalogs/RecruitTags/RecruitTagMultiSelect.vue";
    import RecruitTaskStatusSelect from "../Catalogs/RecruitTaskStatuses/RecruitTaskStatusSelect.vue";

export default {
  name: "RecruitTasks",
  components: {
    RecruitTaskStatusSelect, TablesSkeleton, RecruitTagMultiSelect, PaginationFooter, Kanban, FiltersDrawer, draggable, UserMultiSelect, LocationMultiSelect, RecruitTask, DynamicTables, VacancyMultiSelect },
  mixins: [
    recruitTasks, catalogs, users,
    helpers, dateFormatter, permissions, pageSearchService
  ],
  props: {
    locationId: { type: Number, default: null },
    locationName: { type: String, default: null },
    isIntegrated: { type: Boolean, default: false },
    disabledRouting: { type: Boolean, default: false },
  },
  data() {
    return {
      title: 'Заявки на подбор',
      sortBy: 'id',
      sortDesc: true,
      items: [],
      usersList: [],
      serviceStartField: {
        key: 'selected',
        label: '',
        class: 'text-center',
        thStyle: { width: '50px', 'min-width': '50px' },
      },
      fields: [
        {
          key: 'id',
          label: 'ID',
          thStyle: { width: '40px', 'min-width': '40px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'location_vacancy_name',
          label: 'Вакансия',
          thStyle: { width: '120px', 'min-width': '120px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'location_name',
          thStyle: { width: '100px', 'min-width': '100px' },
          label: 'Объект',
          sortable: true,
          visible: true,
        },
        {
          key: 'assigned',
          thStyle: { width: '120px', 'min-width': '120px' },
          label: 'Ответственный',
          sortable: true,
          visible: true,
        },
        {
          key: 'status_id',
          thStyle: { width: '80px', 'min-width': '80px' },
          label: 'Статус',
          sortable: true,
          visible: true,
        },
        {
          key: 'opened_date',
          thStyle: { width: '110px', 'min-width': '110px' },
          label: 'Дата открытия',
          sortable: true,
          visible: true,
        },
        {
          key: 'closed_date',
          thStyle: { width: '110px', 'min-width': '110px' },
          label: 'Дата закрытия',
          sortable: true,
          visible: true,
        },
        {
          key: 'urgency',
          thStyle: { width: '50px', 'min-width': '50px' },
          label: 'Срочно',
          sortable: true,
          visible: true,
          class: 'text-center'
        },
        {
          key: 'tags',
          thStyle: { width: '200px', 'min-width': '200px' },
          label: 'Теги',
          visible: true,
          sortable: false
        },
      ],
      serviceEndField: {
        key: 'action',
        label: 'Действия',
        thStyle: { width: '80px', 'min-width': '80px' },
      },
      isBusy: false,
      filters: this.getDefaultFilters(),
      deleteRow: {
        id: null,
        location_vacancy_name: '',
      },
      recruitTaskStatuses: [],
      viewMode: 'table',
      kanbanColumns: [],
      popoverShow: false,
      selected: [],
      allSelected: false,
      indeterminate: false,
      allSelectDisabled: true,
      selectedNewStatus: null,
      disableRouting: this.disabledRouting,
      isAddingColumn: false,
    }
  },
  async mounted() {
    await this.configureRefs();
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      if (query.start_opened_date) {
        this.filters.start_opened_date = query.start_opened_date;
      }

      if (query.end_opened_date) {
        this.filters.end_opened_date = query.end_opened_date;
      }

      if (query.start_closed_date) {
        this.filters.start_closed_date = query.start_closed_date;
      }

      if (query.end_closed_date) {
        this.filters.end_closed_date = query.end_closed_date;
      }

      if (query.locations) {
        const decodeLoc = decodeURIComponent(query.locations);
        this.filters.locations = decodeLoc.split(",");
      }

      if (query.users) {
        const decodeLoc = decodeURIComponent(query.users);
        this.filters.users = decodeLoc.split(",");
      }

      if (query.assigned != null) {
        const decodeLoc = decodeURIComponent(query.assigned);
        this.filters.assigned = decodeLoc.split(",");
      }

      if (query.vacancies != null) {
        const decodeLoc = decodeURIComponent(query.vacancies);
        this.filters.vacancies = decodeLoc.split(",");
      }

      if (query.tags != null) {
        const decodeLoc = decodeURIComponent(query.tags);
        this.filters.tags = decodeLoc.split(",");
      }

      if (query.status_id) {
        this.filters.status_id = Number(query.status_id);
      }

      if (query.urgency !== undefined) {
        this.filters.urgency = query.urgency;
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    this.usersList = (await this.getUsersSearch({})).data.items;

    const statusesResult = await this.getRecruitTaskStatuses({});
    this.recruitTaskStatuses = statusesResult && statusesResult.data ? statusesResult.data.items : [];
    this.syncKanbanColumns();

    await this.onSearch();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      const allFields = [
        { ...this.serviceStartField, sticky: 'left' },
        ...this.visibleFields,
        { ...this.serviceEndField, sticky: 'right' }
      ];

      const getWidth = (f) => parseInt(f.thStyle && f.thStyle.width) || 0;

      const lastLeftItem = [...allFields].reverse().find(f => f.sticky === 'left');
      const lastLeftKey = lastLeftItem.key;
      const firstRightItem = allFields.find(f => f.sticky === 'right');
      const firstRightKey = firstRightItem.key;

      return allFields.map((field, index) => {
        const renderedResult = { ...field, thStyle: { ...field.thStyle } };
        const classes = field.class ? field.class.split(' ') : [];
        let offsetStyle = {};

        if (field.sticky === 'left') {
          const offset = allFields.slice(0, index)
            .filter(f => f.sticky === 'left')
            .reduce((sum, f) => sum + getWidth(f), 0);

          classes.push('custom-sticky-column');
          offsetStyle.left = `${offset}px`;
          renderedResult.thStyle.left = `${offset}px`;

          if (field.key === lastLeftKey) classes.push('custom-sticky-left-edge');
        }

        if (field.sticky === 'right') {
          const offset = allFields.slice(index + 1)
            .filter(f => f.sticky === 'right')
            .reduce((sum, f) => sum + getWidth(f), 0);

          classes.push('custom-sticky-column');
          offsetStyle.right = `${offset}px`;
          renderedResult.thStyle.right = `${offset}px`;

          if (field.key === firstRightKey) classes.push('custom-sticky-right-edge');
        }

        renderedResult.class = classes.join(' ');

        const styleObj = {
          ...offsetStyle,
          width: renderedResult.thStyle.width,
          'min-width': renderedResult.thStyle['min-width'],
          'max-width': renderedResult.thStyle['max-width'] || 'none'
        };

        const styleString = Object.keys(styleObj)
          .map(key => `${key}: ${styleObj[key]}`)
          .join('; ');

        renderedResult.tdAttr = {
          style: styleString
        };

        return renderedResult;
      });
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();

      if (this.filters.start_opened_date || this.filters.end_opened_date) {
        tags.push({ key: 'opened_date', label: 'Дата открытия' });
      }

      if (this.filters.start_closed_date || this.filters.end_closed_date) {
        tags.push({ key: 'closed_date', label: 'Дата закрытия' });
      }

      if (this.filters.locations.length > 0) {
        tags.push({ key: 'location', label: `Объекты: ${this.filters.locations.length}`});
      }

      if (this.filters.users.length > 0) {
        tags.push({ key: 'user', label: `Участники: ${this.filters.users.length}`,});
      }

      if (this.filters.status_id !== defaults.status_id) {
        const foundItem = this.statusOptions.find(s => s.value === this.filters.status_id);
        tags.push({ key: 'status', label: foundItem ? foundItem.text : 'Статус заявки' });
      }

      if (this.filters.urgency !== defaults.urgency) {
        tags.push({ key: 'urgency', label: `Срочно: ${this.filters.urgency ? 'Да' : 'Нет'}`});
      }

      if (this.filters.assigned.length > 0) {
        tags.push({ key: 'assigned', label: `Ответственные: ${this.filters.assigned.length}`,});
      }

      if (this.filters.vacancies.length > 0) {
        tags.push({ key: 'vacancy', label: `Вакансии: ${this.filters.vacancies.length}`,});
      }

      if (this.filters.tags.length > 0) {
        tags.push({ key: 'tag', label: `Теги: ${this.filters.tags.length}`,});
      }

      return tags;
    },
    activeKanbanColumns() {
      if (!this.kanbanColumns) return [];

      return this.kanbanColumns
        .filter(c => c.visible)
        .map(c => ({
          id: c.key,
          title: c.label,
          class: c.class
        }));
    },
    activeColumnIds() {
      return this.kanbanColumns.map(c => c.key);
    },
    statusOptions() {
      return [
        { value: null, text: 'Все статусы' },
        ...this.recruitTaskStatuses.map(status => ({
          value: status.id,
          text: status.active ? status.name : `${status.name} (неактивен)`,
        })),
      ];
    },
  },
  watch: {
    '$route.params.id': {
      handler(newId) {
        this.$nextTick(() => {
          if (newId && this.$refs.recruit_task) {
            const isViewonly = this.$route.query.mode === 'view';
            this.$refs.recruit_task.showModal(newId, isViewonly);
          } else if (!newId && this.$refs.recruit_task) {
            this.$refs.recruit_task.hideModal();
          }
        });
      }, immediate: true
    },
    selected(newValue) {
      if (newValue.length === 0) {
        this.indeterminate = false;
        this.allSelected = false;
      } else if (newValue.length === this.totalRows) {
        this.indeterminate = false;
        this.allSelected = true;
      } else {
        this.indeterminate = true;
        this.allSelected = false;
      }
    },
    totalRows(newValue) {
      if (newValue === 0) {
        this.allSelectDisabled = true;
      } else this.allSelectDisabled = newValue > 10000;
    },
    recruitTaskStatuses: {
      handler() {
        this.syncKanbanColumns();
      }, deep: true
    }
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        start_opened_date: this.filters.start_opened_date,
        end_opened_date: this.filters.end_opened_date,
        start_closed_date: this.filters.start_closed_date,
        end_closed_date: this.filters.end_closed_date,
        locations: this.locationId ? [this.locationId] : this.filters.locations,
        users: this.filters.users,
        status_id: this.filters.status_id,
        urgency: this.filters.urgency,
        assigned: this.filters.assigned,
        vacancies: this.filters.vacancies,
        tags: this.filters.tags,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getRecruitTasks(data);
      this.isBusy = false;

      if (!result || !result.data) {
        this.items = [];
        this.isBusy = false;
        this.totalRows = 0;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;

      this.updateSelected();
      this.$emit('refresh');
    },
    async getAllRecruitTasks() {
      const data = {
        page: 1,
        size: this.totalRows,
        start_opened_date: this.filters.start_opened_date,
        end_opened_date: this.filters.end_opened_date,
        start_closed_date: this.filters.start_closed_date,
        end_closed_date: this.filters.end_closed_date,
        locations: this.filters.locations,
        users: this.filters.users,
        status_id: this.filters.status_id,
        urgency: this.filters.urgency,
        assigned: this.filters.assigned,
        vacancies: this.filters.vacancies,
        tags: this.filters.tags,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      const result = await this.getRecruitTasks(data);

      if (result.data && result.data.items.length > 0) {
        return result.data.items;
      }

      return [];
    },
    showModal(recruitTaskId, isViewonly) {
      const canEdit = this.canEditRecruitTasks();

      if (this.isIntegrated) {
        this.$refs.recruit_task.showModal(recruitTaskId || null, isViewonly && !canEdit);
        return;
      }

      if (!recruitTaskId) {
        if (!canEdit) return;

        this.$refs.recruit_task.showModal(null, false);
        this.onPopoverClose();
        return;
      }

      const isView = isViewonly || !canEdit;
      const query = isView ? { mode: 'view' } : {};
      this.onPopoverClose();

      this.$router.push({
        name: 'RecruitTasks',
        params: { id: recruitTaskId },
        query: query
      });
    },
    onRowDblClicked(item) {
      this.showModal(item.id, false);
    },
    onModalClosed() {
      if (this.isIntegrated) return;

      if (this.$route.params.id) {
        this.$router.replace({
          name: 'RecruitTasks',
          params: { id: null },
          hash: this.$route.hash,
        }).catch(()=>{});
      }
    },
    getAssignedName(userId) {
      if (!userId) return null;

      const user = this.usersList.find(u => u.id === userId);
      if (!user) { return '' }
      return user.name;
    },
    getStatusText(statusId) {
      const option = this.statusOptions.find(opt => opt.value === statusId);
      return option ? option.text : '';
    },
    onRemove(row) {
      if (!this.canEditRecruitTasks()) return;

      if (row.item) {
        this.deleteRow = row.item
      }
      const title = "Удаление заявки";
      const message = `Вы действительно хотите удалить заявку "${this.deleteRow.location_vacancy_name}"?`;
      this.showModalDelete(title, message, async () => {
        const result = await this.deleteRecruitTask(this.deleteRow);

        if (result.error) {
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Заявка удалена.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    // Filters
    getDefaultFilters() {
      return {
        query: '',
        start_opened_date: null,
        end_opened_date: null,
        start_closed_date: null,
        end_closed_date: null,
        locations: [],
        users: [],
        status_id: null,
        urgency: null,
        assigned: [],
        vacancies: [],
        tags: []
      };
    },
    async onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) return;

      this.filters = filters;
      await this.configureRefs();

      await this.onSearch();
    },
    removeFilterTag(tag) {
      if (tag.key === 'opened_date') {
        this.filters.start_opened_date = null;
        this.filters.end_opened_date = null;
      }
      else if (tag.key === 'closed_date') {
        this.filters.start_closed_date = null;
        this.filters.end_closed_date = null;
      }
      else if (tag.key === 'location') {
        this.filters.locations = [];
        this.$refs.location_multi_select.onClearSelected();
      }
      else if (tag.key === 'user') {
        this.filters.locations = [];
        this.$refs.user_multi_select.onClearSelected();
      }
      else if (tag.key === 'status') {
        this.filters.status_id = null;
      }
      else if (tag.key === 'urgency') {
        this.filters.urgency = null;
      }
      else if (tag.key === 'assigned') {
        this.filters.assigned = [];
        this.$refs.user_multi_select_assigned.onClearSelected();
      }
      else if (tag.key === 'vacancy') {
        this.filters.vacancies = [];
        this.$refs.vacancy_multi_select.onClearSelected();
      }
      else if (tag.key === 'tag') {
        this.filters.tags = [];
        this.$refs.tag_multi_select.onClearSelected();
      }

      this.onSearch();
    },
    async configureRefs() {
      if (this.$refs.user_multi_select != null) {
        await this.$refs.user_multi_select.configure(this.filters.users);
      }

      if (this.$refs.user_multi_select_assigned != null) {
        await this.$refs.user_multi_select_assigned.configure(this.filters.assigned);
      }

      if (this.$refs.location_multi_select != null) {
        await this.$refs.location_multi_select.configure(this.filters.locations);
      }

      if (this.$refs.vacancy_multi_select != null) {
        await this.$refs.vacancy_multi_select.configure(this.filters.vacancies);
      }
    },
    onSelectLocation(locations) {
      this.filters.locations = locations.map(item => { return item.id; });
    },
    onSelectUser(users) {
      this.filters.users = users.map(item => { return item.id; });
    },
    onSelectUserAssigned(users) {
      this.filters.assigned = users.map(item => { return item.id; });
    },
    onSelectVacancy(vacancies) {
      this.filters.vacancies = vacancies.map(item => { return item.id; });
    },
    onSelectTag(tags) {
      this.filters.tags = tags.map(item => { return item.id; });
    },
    // Kanban
    getKanbanBadgeClass(index) {
      const classes = ['tag-secondary', 'tag-info', 'tag-primary', 'tag-success', 'tag-warning'];
      return classes[index % classes.length];
    },
    toggleView() {
      this.viewMode = this.viewMode === 'table' ? 'kanban' : 'table';
      this.selected = [];
      this.updateSelected();
    },
    syncKanbanColumns(newColId = null) {
      const deletedCols = JSON.parse(localStorage.getItem('recruit_tasks_deleted_cols') || '[]');

      this.kanbanColumns = this.recruitTaskStatuses
        .filter(status => !deletedCols.includes(status.id))
        .map((status, index) => {
          const existing = this.kanbanColumns.find(c => c.key === status.id);
          const isVisible = (status.id === newColId) || (existing ? existing.visible : true);

          return {
            key: status.id,
            label: status.name,
            visible: isVisible,
            class: this.getKanbanBadgeClass(index)
          };
        });
    },
    handleAddKanbanColumn(selectedStatus) {
      if (!selectedStatus) return;
      this.isAddingColumn = false;

      let deletedCols = JSON.parse(localStorage.getItem('recruit_tasks_deleted_cols') || '[]');
      deletedCols = deletedCols.filter(id => id !== selectedStatus.id);
      localStorage.setItem('recruit_tasks_deleted_cols', JSON.stringify(deletedCols));

      if (!this.recruitTaskStatuses.find(s => s.id === selectedStatus.id)) {
        this.recruitTaskStatuses.push(selectedStatus);
      }

      this.syncKanbanColumns(selectedStatus.id);
    },
    handleDeleteKanbanColumn(field) {
      const index = this.kanbanColumns.findIndex(c => c.key === field.key);

      if (index !== -1) {
        this.kanbanColumns.splice(index, 1);
      }

      let deletedCols = JSON.parse(localStorage.getItem('recruit_tasks_deleted_cols') || '[]');

      if (!deletedCols.includes(field.key)) {
        deletedCols.push(field.key);
        localStorage.setItem('recruit_tasks_deleted_cols', JSON.stringify(deletedCols));
      }
    },
    async kanbanUpdateStatus({ item, newStatus, oldStatus }) {
      this.isBusy = true;

      const result = await this.getRecruitTaskById(item.id);

      if (!result || !result.data) {
        this.showError('Не удалось получить данные заявки.');
        this.isBusy = false;
        return;
      }

      const data = { ...result.data };
      data.status_id = newStatus;

      if (data.members) {
        data.members = data.members.map(m => ({ user_id: m.user_id || m.id }));
      }

      if (data.tags) {
        data.tags = data.tags.map(tag => ({ id: tag.id }));
      }

      if (data.opened_date) {
        data.opened_date = this.formattedToServerDate(data.opened_date);
      }

      if (data.closed_date) {
        data.closed_date = this.formattedToServerDate(data.closed_date);
      }

      delete data.selected;

      const putResult = await this.putRecruitTask(data);

      if (!putResult || !putResult.data) {
        this.showError('Не удалось обновить статус.');

        const task = this.items.find(t => t.id === item.id);

        if (task) {
          task.status_id = oldStatus;
          task.status_name = this.getStatusText(oldStatus);
        }

        await this.getItems();
      } else {
        this.showToast('Статус заявки обновлён.', 'Успех', 'success');

        const task = this.items.find(t => t.id === item.id);

        if (task) {
          task.status_id = newStatus;
          task.status_name = this.getStatusText(newStatus);
        }
      }

      this.isBusy = false;
    },
    // Update status
    async selectAllRows(checked) {
      if (checked) {
        if (this.totalRows > 10000) {
          this.showSubmitError("Большое количество записей. Отфильтруйте данные, например, по периоду.");
          this.allSelected = false;
          return;
        }

        this.selected = await this.getAllRecruitTasks();
        this.updateSelected();
      } else {
        this.selected = [];
        this.updateSelected();
      }
    },
    rowClicked(item) {
      // Ищем уже выбранные
      const existItem = this.selected.findIndex(e => e.id === item.id);

      if (existItem !== -1) {
        this.selected.splice(existItem, 1);
        this.$set(item, 'selected', false);
      } else {
        this.selected.push(item);
        this.$set(item, 'selected', true);
      }
    },
    updateSelected() {
      if (!this.items) return;

      this.items.forEach((item) => {
        const isSelected = this.selected.some(e => e.id === item.id);
        this.$set(item, 'selected', isSelected);
      });
    },
    tbodyRowClass(item) {
      return item && item.selected ? ["b-table-row-selected", "table-primary", "pointer-cell"] : ["pointer-cell"];
    },
    onShown() {
      if (this.$refs.allTasksStatusSelect) {
        this.focusRef(this.$refs.allTasksStatusSelect);
      }
    },
    onHidden() {
      if (this.$refs.allTasksStatusSelect) {
        this.focusRef(this.$refs.allTasksStatusSelect);
      }
    },
    focusRef(ref) {
      this.$nextTick(() => {
        this.$nextTick(() => {
          (ref.$el || ref).focus();
        })
      })
    },
    onPopoverClose() {
      this.selectedNewStatus = null;
      this.popoverShow = false;
    },
    async onPopoverOk() {
      const recruitTasksIds = this.selected.map(item => item.id);

      const data = {
        recruit_task_ids: recruitTasksIds,
        status_id: this.selectedNewStatus
      };

      this.isBusy = true;
      const result = await this.putRecruitTaskUpdateStatuses(data);
      this.isBusy = false;

      if (result.error) {
        this.showError(result.error.message);
      } else {
        this.showToast(`Статус успешно обновлен для ${recruitTasksIds.length} заявок.`, 'Успех', 'success');
        this.selected = [];
        this.allSelected = false;
        this.onPopoverClose();
        await this.getItems();
      }
    },
    handleJobPosting(recruitTaskId) {
      if (!this.canEditRecruitJobPostings()) return;

      this.$router.push({
        name: 'RecruitJobPostings',
        params: {
          recruitTaskId: recruitTaskId,
          recruitTaskDisabled: true
        }
      });
    },
    // Permissions
    canEditRecruitTasks() {
      return this.hasPermission([PERM.RECRUIT_TASK_EDIT]);
    },
    canEditRecruitJobPostings() {
      return this.hasPermission([PERM.RECRUIT_JOB_POSTING_EDIT]);
    }
  },
}
</script>

<style>
#recruit-tasks td {
  vertical-align: middle;
}

.close {
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/components/Recruiting/RecruitTasks.vue